import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  Divider,
  Dropdown,
  MetricCard,
  Spinner,
} from '@makeably/creativex-design-system';
import MediaBreakdown from 'components/creative_campaigns/MediaBreakdown';
import {
  channelDimensionProps,
  dateOptionStorageKey,
  filterStorageKey,
  renderSpendMetric,
} from 'components/creative_campaigns/shared';
import { getOptions } from 'components/molecules/FilterButton';
import FilterWithTags from 'components/molecules/FilterWithTags';
import ItemsTable from 'components/molecules/ItemsTable';
import { addErrorToast } from 'components/organisms/Toasts';
import { getPlacementLabel } from 'components/reporting/creativeLifecycleUtilities';
import {
  attributeLookupsProps,
  dateOptionProps,
} from 'components/reporting/shared';
import { getObjFilterTest } from 'utilities/filtering';
import { getItemSortBy } from 'utilities/item';
import { useViewPage } from 'utilities/mixpanel';
import { rate } from 'utilities/number';
import { objectGroupBy } from 'utilities/object';
import { get } from 'utilities/requests';
import {
  auditCreativesPath,
  campaignRecordsCreativeCampaignPath,
  dashboardRecordsCreativeCampaignsPath,
} from 'utilities/routes';
import {
  retrieveFromLocalStorage,
  saveToLocalStorage,
} from 'utilities/storage';
import {
  toConciseCount,
  toConciseSpend,
  toPercent,
} from 'utilities/string';
import styles from './CampaignDetails.module.css';

const propTypes = {
  campaignId: PropTypes.number.isRequired,
  dateOptions: PropTypes.arrayOf(dateOptionProps).isRequired,
  defaultDateOption: dateOptionProps.isRequired,
  lookups: attributeLookupsProps.isRequired,
  scoreDetails: PropTypes.shape({
    id: PropTypes.number,
    rateLabel: PropTypes.string,
    spendRateLabel: PropTypes.string,
  }).isRequired,
  supportedChannels: PropTypes.objectOf(channelDimensionProps).isRequired,
};

const segments = [
  {
    label: 'Ad Format',
    value: 'adFormat',
  },
  {
    label: 'Brand',
    value: 'brand',
  },
  {
    label: 'Channel',
    value: 'channel',
  },
  {
    label: 'Creator Type',
    value: 'contentType',
  },
  {
    label: 'Market',
    value: 'market',
  },
  {
    label: 'Placement',
    value: 'platformPlacements',
  },
];

function getHeaders(scoreDetails) {
  return [
    {
      key: 'market',
      label: 'Market',
    },
    {
      key: 'spend',
      label: 'Spend',
    },
    {
      key: `scoreRate::${scoreDetails.id}`,
      label: scoreDetails.rateLabel,
    },
    {
      key: `spendRate::${scoreDetails.id}`,
      label: scoreDetails.spendRateLabel,
    },
    {
      key: 'posts',
      label: 'Posts',
    },
    {
      key: 'channels',
      label: 'Channels',
    },
    {
      key: 'adFormats',
      label: 'Ad Formats',
    },
    {
      key: 'placements',
      label: 'Placements',
    },
    {
      key: 'creatorTypes',
      label: 'Creator Types',
    },
    {
      key: 'impressions',
      label: 'Impressions',
    },
  ];
}

function getMetrics(scoreDetails) {
  return [
    {
      label: 'Spend',
      value: 'spend',
    },
    {
      format: 'percentage',
      label: scoreDetails.rateLabel,
      value: `scoreRate::${scoreDetails.id}`,
    },
    {
      format: 'percentage',
      label: scoreDetails.spendRateLabel,
      value: `spendRate::${scoreDetails.id}`,
    },
    {
      label: 'Markets',
      value: 'markets',
    },
    {
      label: 'Posts',
      value: 'posts',
    },
    {
      label: 'Assets',
      value: 'assets',
    },
  ];
}

function getInitialDateOption(dateOptions, initialDateOption) {
  const storedDateOption = retrieveFromLocalStorage(dateOptionStorageKey);
  if (storedDateOption && dateOptions.some((option) => option.value === storedDateOption.value)) {
    return storedDateOption;
  }
  return initialDateOption;
}

function getInitialFilters() {
  const storedFilters = retrieveFromLocalStorage(filterStorageKey);
  if (storedFilters) {
    const initialFilters = {};
    const filterKeys = segments.map((segment) => segment.value);
    Object.keys(storedFilters).forEach((key) => {
      if (filterKeys.includes(key)) {
        initialFilters[key] = storedFilters[key];
      }
    });
    return initialFilters;
  }
  return {};
}

async function getRecords(dateOption) {
  const params = {
    date_type: dateOption.type,
    start_date: dateOption.startDate,
  };

  return get(dashboardRecordsCreativeCampaignsPath(params));
}

async function getCampaignRecords(id, dateOption) {
  const params = {
    date_range: dateOption.value,
    date_type: dateOption.type,
    start_date: dateOption.startDate,
  };

  return get(campaignRecordsCreativeCampaignPath(id, params));
}

function processRecord(record, lookups) {
  const platformPlacements = (record.placements ?? []).map((p) => (
    getPlacementLabel(p.publisher, p.placement)
  ));

  return {
    ...record,
    adFormat: lookups.adFormat[record.adFormatKey],
    brand: lookups.brand[record.brandId],
    channel: lookups.channel[record.channelId],
    market: lookups.market[record.marketId],
    placement: getPlacementLabel(record.publisher, record.placement),
    platformPlacements,
  };
}

function getMarketItems(recordGroup, scoreId) {
  const { market } = recordGroup[0];

  const data = recordGroup.reduce(
    ({
      spend,
      qualitySpend,
      qualityPostCount,
      postCount,
      channelSet,
      adFormatSet,
      placementSet,
      creatorTypeSet,
      impressions,
    },
    record) => {
      const qualityPost = record[`rank::${scoreId}`] === 'highest';

      return {
        adFormatSet: record.adFormat ? new Set([...adFormatSet, record.adFormat]) : adFormatSet,
        channelSet: record.channel ? new Set([...channelSet, record.channel]) : channelSet,
        creatorTypeSet:
          record.contentType ? new Set([...creatorTypeSet, record.contentType]) : creatorTypeSet,
        impressions: impressions + (record.impressions ?? 0),
        placementSet: new Set([...placementSet, ...(record.platformPlacements ?? [])]),
        postCount: postCount + (record.postCount ?? 0),
        qualityPostCount: qualityPostCount + (qualityPost ? record.postCount : 0),
        qualitySpend: qualitySpend + (record[`excellentSpend::${scoreId}`] ?? 0),
        spend: spend + (record.spend ?? 0),
      };
    }, {
      adFormatSet: new Set(),
      channelSet: new Set(),
      creatorTypeSet: new Set(),
      impressions: 0,
      placementSet: new Set(),
      postCount: 0,
      qualityPostCount: 0,
      qualitySpend: 0,
      spend: 0,
    },
  );

  const scoreRate = rate(data.qualityPostCount, data.postCount);
  const spendRate = rate(data.qualitySpend, data.spend);

  return {
    adFormats: {
      value: data.adFormatSet.size,
    },
    channels: {
      value: data.channelSet.size,
    },
    creatorTypes: {
      value: data.creatorTypeSet.size,
    },
    id: { value: market },
    impressions: {
      label: toConciseCount(data.impressions),
      value: data.impressions,
    },
    market: { value: market },
    placements: {
      value: data.placementSet.size,
    },
    posts: {
      label: toConciseCount(data.postCount),
      value: data.postCount,
    },
    spend: {
      label: toConciseSpend(data.spend),
      value: data.spend,
    },
    [`scoreRate::${scoreId}`]: {
      label: toPercent(scoreRate) ?? 'N/A',
      value: scoreRate,
    },
    [`spendRate::${scoreId}`]: {
      label: toPercent(spendRate) ?? 'N/A',
      value: spendRate,
    },
  };
}

function getItems(records, scoreId) {
  const groupedRecords = objectGroupBy(records, (record) => record.market);

  return Object.values(groupedRecords).map((group) => getMarketItems(group, scoreId));
}

function getMetricTotals(processedRecords, scoreId) {
  return processedRecords.reduce(
    ({
      assetSet,
      impressions,
      marketSet,
      postCount,
      qualityPostCount,
      qualitySpend,
      spend,
    }, record) => {
      const qualityPost = record[`rank::${scoreId}`] === 'highest';

      return {
        assetSet: new Set([...assetSet, ...(record.assetIds ?? [])]),
        impressions: impressions + (record.impressions ?? 0),
        marketSet: record.marketId ? new Set([...marketSet, record.marketId]) : marketSet,
        postCount: postCount + (record.postCount ?? 0),
        qualityPostCount: qualityPostCount + (qualityPost ? record.postCount : 0),
        qualitySpend: qualitySpend + (record[`excellentSpend::${scoreId}`] ?? 0),
        spend: spend + (record.spend ?? 0),
      };
    }, {
      assetSet: new Set(),
      impressions: 0,
      marketSet: new Set(),
      postCount: 0,
      qualityPostCount: 0,
      qualitySpend: 0,
      spend: 0,
    },
  );
}

function calculateMetrics(records, scoreDetails) {
  const totals = getMetricTotals(records, scoreDetails.id);

  const scoreRate = totals.postCount ? totals.qualityPostCount / totals.postCount : 0;
  const spendRate = totals.spend ? totals.qualitySpend / totals.spend : 0;

  return {
    assets: totals.assetSet.size,
    impressions: totals.impressions,
    markets: totals.marketSet.size,
    posts: totals.postCount,
    spend: totals.spend,
    [`scoreRate::${scoreDetails.id}`]: scoreRate,
    [`spendRate::${scoreDetails.id}`]: spendRate,
  };
}

function getMetricValue(metric, processedRecords, filteredMetrics) {
  if (!processedRecords.length) return 'No data';

  const { value } = metric;
  const metricValue = filteredMetrics[value];

  switch (value) {
    case 'spend':
      return toConciseSpend(metricValue);
    case 'posts':
    case 'assets':
      return toConciseCount(metricValue);
    default:
      return metricValue;
  }
}

function getCaption(metric, filteredValue, overallValue) {
  const metricsWithCaption = ['spend', 'markets', 'posts'];
  if (metricsWithCaption.includes(metric)) {
    const percent = overallValue ? (filteredValue / overallValue) : 0;
    return `${toPercent(percent)} of total`;
  } else if (metric === 'assets') {
    return (
      <a href={auditCreativesPath()} rel="noreferrer" target="_blank">
        View All
      </a>
    );
  }
  return null;
}

function renderEmptyContent() {
  return (
    <div className="t-empty">
      <p>No data available</p>
    </div>
  );
}

function CampaignDetails({
  campaignId,
  dateOptions,
  defaultDateOption,
  lookups,
  scoreDetails,
  supportedChannels,
}) {
  useViewPage();

  const [allRecords, setAllRecords] = useState([]);
  const [campaignRecords, setCampaignRecords] = useState([]);
  const initialDateOption = getInitialDateOption(dateOptions, defaultDateOption);
  const [selectedDateOption, setSelectedDateOption] = useState(initialDateOption);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState(getInitialFilters());
  const [sort, setSort] = useState({
    asc: false,
    key: 'spend',
  });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const hasFilters = Object.keys(filters).length > 0;

  useEffect(() => {
    saveToLocalStorage(selectedDateOption, dateOptionStorageKey);
  }, [selectedDateOption]);

  useEffect(() => {
    saveToLocalStorage(filters, filterStorageKey);
  }, [filters]);

  useEffect(() => {
    const fetchRecords = async () => {
      setLoading(true);
      try {
        const responses = await Promise.all([
          getRecords(selectedDateOption),
          getCampaignRecords(campaignId, selectedDateOption),
        ]);

        if (responses.some((response) => response.isError)) {
          const isTimeout = responses.some((resp) => resp.status === 504);
          const message = isTimeout ? 'The data request has timed out' : 'The data could not be loaded';
          addErrorToast(message);
        } else {
          setAllRecords(responses[0].data);
          setCampaignRecords(responses[1].data);
        }
      } catch (error) {
        addErrorToast('An unexpected error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchRecords();
  }, [selectedDateOption]);

  const processedRecords = useMemo(() => (
    campaignRecords.map((record) => processRecord(record, lookups))
  ), [campaignRecords, lookups]);

  const overallMetrics = useMemo(() => (
    calculateMetrics(allRecords, scoreDetails)
  ), [allRecords, scoreDetails]);

  const filterOptions = useMemo(() => (
    getOptions(processedRecords, segments)
  ), [processedRecords]);

  const filteredRecords = useMemo(() => {
    if (hasFilters) {
      const filterTest = getObjFilterTest(filters);
      return processedRecords.filter(filterTest);
    }
    return processedRecords;
  }, [processedRecords, filters, hasFilters]);

  const filteredPostRecords = useMemo(() => (
    filteredRecords.filter((record) => record.level === 'post')
  ), [filteredRecords]);

  const filteredMetrics = useMemo(() => (
    calculateMetrics(filteredPostRecords, scoreDetails)
  ), [filteredPostRecords, scoreDetails]);

  const items = useMemo(() => (
    getItems(filteredPostRecords, scoreDetails.id)
  ), [filteredPostRecords, scoreDetails]);

  const sortedItems = useMemo(() => {
    const byKeyDir = getItemSortBy(sort.key, sort.asc);
    return items.slice().sort(byKeyDir);
  }, [items, sort]);

  const marketSpends = useMemo(() => {
    const itemSpends = items.map((item) => item.spend.value || 0);
    return {
      max: itemSpends.length > 0 ? Math.max(...itemSpends) : null,
      min: itemSpends.length > 0 ? Math.min(...itemSpends) : null,
    };
  }, [items]);

  const renderMetricCard = (metric) => {
    const value = getMetricValue(metric, processedRecords, filteredMetrics);
    const caption = getCaption(
      metric.value,
      filteredMetrics[metric.value],
      overallMetrics[metric.value],
    );

    return (
      <div key={metric.value} className={styles.metricCard}>
        <MetricCard
          alert={false}
          metric={{
            ...metric,
            caption,
            value,
          }}
        />
      </div>
    );
  };

  const renderMetricCards = () => {
    if (processedRecords.length > 0) {
      return getMetrics(scoreDetails).map(renderMetricCard);
    }
    return renderEmptyContent();
  };

  if (loading) {
    return (
      <div className="u-flexColumn u-alignCenter u-gap-16">
        <Spinner />
        <p>Loading campaign details...</p>
      </div>
    );
  }

  return (
    <div className="u-flexColumn u-gap-16">
      <div className="u-flexRow u-justifyBetween">
        <FilterWithTags
          filterOpen={filterOpen}
          filterOptions={filterOptions}
          filters={filters}
          segments={segments}
          onClose={() => setFilterOpen(false)}
          onFilterChange={setFilters}
          onOpen={() => setFilterOpen(true)}
        />
        <Dropdown
          menuProps={{ size: 'small' }}
          options={dateOptions}
          selected={selectedDateOption}
          size="small"
          onChange={setSelectedDateOption}
        />
      </div>
      <Card className="u-flexColumn u-gap-16">
        <h5>Overview</h5>
        <div className="u-flexRow u-justifyBetween">
          { renderMetricCards() }
        </div>
      </Card>
      <Card className="u-flexColumn u-gap-24">
        <h5>Market Usage</h5>
        <div className="u-flexRow u-gap-24">
          { renderSpendMetric('Lowest Market Spend', marketSpends.min) }
          { renderSpendMetric('Highest Market Spend', marketSpends.max) }
        </div>
        <Divider />
        <ItemsTable
          className="u-scrollShadowRight"
          emptyTableContent={renderEmptyContent()}
          headers={getHeaders(scoreDetails)}
          items={sortedItems}
          page={page}
          perPage={10}
          sort={sort}
          onPageChange={setPage}
          onSortChange={setSort}
        />
      </Card>
      <MediaBreakdown
        filteredRecords={filteredRecords}
        overallImpressions={overallMetrics.impressions}
        scoreDetails={scoreDetails}
        supportedChannels={supportedChannels}
      />
    </div>
  );
}

CampaignDetails.propTypes = propTypes;

export default CampaignDetails;
